/**
 * heavy-asset-manager.js — opt-in download flow for >20 MB ML/AI assets.
 *
 * Goals (all enforced by this module so each tool doesn't re-implement them):
 *
 *   1. Device gate — calls `__deviceCap.canRunHeavyML(sizeMB, opts)` and
 *      refuses preparation when device too weak. Caller should NOT show
 *      the option in UI in that case (memory: «Скрыть полностью»).
 *
 *   2. User consent — modal dialog before downloading anything > 20 MB,
 *      with model name, size, ETA estimate at user's connection speed.
 *      One-time decision per asset key (remembered in localStorage so a
 *      re-visit doesn't re-prompt).
 *
 *   3. Download progress — XHR with progress events; emits to caller via
 *      `opts.onProgress(0..1)`. Cancellable via AbortSignal.
 *
 *   4. SW cache — relies on the existing Service Worker cache (sw.js)
 *      to persist binary data across visits. We don't add a separate
 *      cache layer here — duplicating storage would burn quota for
 *      no benefit. Instead, downloads hit the network on first prepare
 *      and are served from SW cache on subsequent prepares (zero bytes).
 *
 *   5. Telemetry — every consent show / accept / decline / download
 *      progress / completion / failure goes through `__ae` with a
 *      consistent `heavy_asset_*` namespace.
 *
 * Public API:
 *   __heavyAsset.prepare(key, opts): Promise<{urls: {<file>: <usedUrl>}, fromCache}>
 *   __heavyAsset.getStatus(key): 'unavailable' | 'available' | 'consented' | 'declined' | 'loaded'
 *   __heavyAsset.resetConsent(key): void  // for testing or "ask me again" UI
 *
 * `prepare()` does NOT actually instantiate the model — it just ensures the
 * binary URLs are reachable + the user has consented + the SW cache will
 * serve them quickly. The caller (each tool's worker) handles the actual
 * `WebAssembly.instantiate` / `ort.InferenceSession.create`.
 *
 * URL routing convention (from reference_dfn3_hf_mirror_2026_05_07.md):
 *   - opts.urls       = primary URL map  (RU origin for RU users, HF for non-RU)
 *   - opts.urlsFallback = fallback URL map (HF for RU; RU origin abs URL for non-RU)
 *   - Tool blade computes both via `app('asset-router')->heavy(...)` /
 *     `heavyFallback(...)` and passes them in.
 *
 * On RKN-blocked external hits, fallback fires automatically; we report
 * `usedFallback: true` in completion telemetry so dashboards can spot
 * elevated fallback rates by geo (signal that primary mirror is broken
 * or being throttled — same class of bug as DFN3 GH→HF migration).
 */
(function(){
    'use strict';

    if (window.__heavyAsset) return;

    var CONSENT_KEY_PREFIX = 'heavy_asset_consent_v1:'; // bump v on schema change
    var SIZE_PROMPT_THRESHOLD_MB = 20;                  // below this, no prompt
    var STATUS = {
        UNAVAILABLE: 'unavailable',  // device gate failed
        AVAILABLE:   'available',    // OK to prompt
        CONSENTED:   'consented',    // user said yes (current or past visit)
        DECLINED:    'declined',     // user said no this visit
        LOADED:      'loaded'        // already prepared this session
    };

    // Per-session "loaded" cache — survives until reload.
    var _loaded = Object.create(null);
    // Per-session "declined" cache — re-prompt allowed only if caller asks.
    var _declinedThisSession = Object.create(null);

    // Built-in dialog labels (9 locales, matches config('app.supported_locales'))
    var L = {
        en: { dl: 'Download', cancel: 'Cancel', size: 'Size', oneTime: 'One-time download (cached for next visits).', headerOff: 'Download AI model?', netSlow: 'Slow connection detected — this may take a while.' },
        ru: { dl: 'Скачать', cancel: 'Отмена', size: 'Размер', oneTime: 'Скачать один раз (закэшируется для следующих посещений).', headerOff: 'Скачать AI-модель?', netSlow: 'Медленное соединение — загрузка может занять время.' },
        ko: { dl: '다운로드', cancel: '취소', size: '크기', oneTime: '한 번만 다운로드 (다음 방문에 캐시됨).', headerOff: 'AI 모델 다운로드?', netSlow: '느린 연결 — 시간이 걸릴 수 있습니다.' },
        ar: { dl: 'تحميل', cancel: 'إلغاء', size: 'الحجم', oneTime: 'تحميل لمرة واحدة (يُخزّن مؤقتًا للزيارات القادمة).', headerOff: 'تحميل نموذج الذكاء الاصطناعي؟', netSlow: 'اتصال بطيء — قد يستغرق التحميل وقتًا.' },
        id: { dl: 'Unduh', cancel: 'Batal', size: 'Ukuran', oneTime: 'Unduh sekali (di-cache untuk kunjungan berikutnya).', headerOff: 'Unduh model AI?', netSlow: 'Koneksi lambat — proses unduhan mungkin memakan waktu.' },
        pt: { dl: 'Baixar', cancel: 'Cancelar', size: 'Tamanho', oneTime: 'Baixar uma vez (em cache nas próximas visitas).', headerOff: 'Baixar modelo de IA?', netSlow: 'Conexão lenta — o download pode demorar.' },
        de: { dl: 'Herunterladen', cancel: 'Abbrechen', size: 'Größe', oneTime: 'Einmaliger Download (für künftige Besuche zwischengespeichert).', headerOff: 'KI-Modell herunterladen?', netSlow: 'Langsame Verbindung — Download kann dauern.' },
        es: { dl: 'Descargar', cancel: 'Cancelar', size: 'Tamaño', oneTime: 'Descargar una vez (en caché para próximas visitas).', headerOff: '¿Descargar modelo de IA?', netSlow: 'Conexión lenta — la descarga puede tardar.' },
        fr: { dl: 'Télécharger', cancel: 'Annuler', size: 'Taille', oneTime: 'Téléchargement unique (mis en cache pour les prochaines visites).', headerOff: 'Télécharger le modèle IA ?', netSlow: 'Connexion lente — le téléchargement peut être long.' },
        uk: { dl: 'Завантажити', cancel: 'Скасувати', size: 'Розмір', oneTime: 'Одне завантаження (кешується для наступних відвідувань).', headerOff: 'Завантажити модель ШІ?', netSlow: 'Повільне з’єднання — завантаження може тривати довго.' },
        tr: { dl: 'İndir', cancel: 'İptal', size: 'Boyut', oneTime: 'Tek seferlik indirme (sonraki ziyaretler için önbelleğe alınır).', headerOff: 'Yapay zekâ modeli indirilsin mi?', netSlow: 'Yavaş bağlantı algılandı — bu biraz zaman alabilir.' }
    };
    function t(k){ var loc = window.__LOCALE || 'en'; return (L[loc] || L.en)[k]; }

    function _hasConsent(key) {
        try {
            return localStorage.getItem(CONSENT_KEY_PREFIX + key) === '1';
        } catch(_) { return false; }
    }
    function _setConsent(key, val) {
        try {
            if (val) localStorage.setItem(CONSENT_KEY_PREFIX + key, '1');
            else     localStorage.removeItem(CONSENT_KEY_PREFIX + key);
        } catch(_) {}
    }

    function emitTele(action, payload) {
        if (typeof window.__ae !== 'function') return;
        try { window.__ae('heavy_asset_' + action, payload || {}); } catch(_){}
    }

    /**
     * Show the consent modal. Returns Promise<bool>.
     *
     * The modal is built in vanilla JS — no Blade dependency, so it works
     * inside any tool. Styled to match the project's liquid-glass look via
     * inherited CSS variables (--lg-surface, --accent, etc).
     */
    function _showConsent(opts) {
        return new Promise(function(resolve){
            var slug = opts.slug || 'unknown';
            var key = opts.key;
            var sizeMB = opts.sizeMB;
            var modelLabel = opts.modelLabel || key;
            var modelDesc = opts.modelDesc || '';

            // Slow connection warning
            var connTier = (window.__deviceCap && window.__deviceCap.get && window.__deviceCap.get().connectionTier) || 'unknown';
            var slowWarn = (connTier === 'slow' || connTier === 'very-slow') ? t('netSlow') : '';

            emitTele('consent_show', { slug: slug, key: key, sizeMB: sizeMB, conn: connTier });

            var overlay = document.createElement('div');
            overlay.className = 'ham-overlay';
            overlay.setAttribute('role', 'dialog');
            overlay.setAttribute('aria-modal', 'true');
            overlay.setAttribute('aria-labelledby', 'ham-title');
            overlay.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,.55);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);z-index:2147483645;display:flex;align-items:center;justify-content:center;padding:16px;animation:hamIn .18s ease-out';
            // Modal box
            var box = document.createElement('div');
            box.style.cssText = 'background:var(--bg-secondary,#1a1f2e);border:1px solid var(--lg-border,rgba(255,255,255,.08));border-radius:18px;padding:24px;max-width:440px;width:100%;color:var(--text-primary,#e6e7ea);box-shadow:0 20px 60px rgba(0,0,0,.4);font-family:inherit';
            box.innerHTML = ''
                + '<style>@keyframes hamIn{from{opacity:0;transform:scale(.96)}to{opacity:1;transform:scale(1)}}</style>'
                + '<h3 id="ham-title" style="margin:0 0 12px;font-size:18px;font-weight:600">' + _esc(t('headerOff')) + '</h3>'
                + '<div style="font-size:14px;color:var(--text-secondary,#a8adb9);line-height:1.55;margin-bottom:16px"><strong style="color:var(--text-primary,#e6e7ea);font-weight:600">' + _esc(modelLabel) + '</strong>'
                + (modelDesc ? '<br>' + _esc(modelDesc) : '')
                + '</div>'
                + '<div style="display:flex;gap:14px;margin-bottom:14px;font-size:13px"><span style="color:var(--text-muted,#6b7280)">' + _esc(t('size')) + ':</span> <strong>' + sizeMB + ' MB</strong></div>'
                + '<p style="font-size:12px;color:var(--text-muted,#6b7280);margin:0 0 18px;line-height:1.5">' + _esc(t('oneTime')) + (slowWarn ? '<br><span style="color:#fbbf24">⚠ ' + _esc(slowWarn) + '</span>' : '') + '</p>'
                + '<div style="display:flex;gap:10px;justify-content:flex-end">'
                + '  <button type="button" class="ham-cancel" style="padding:9px 18px;border:1px solid var(--lg-border,rgba(255,255,255,.12));background:transparent;color:var(--text-secondary,#a8adb9);border-radius:10px;font-family:inherit;font-size:14px;cursor:pointer">' + _esc(t('cancel')) + '</button>'
                + '  <button type="button" class="ham-ok" style="padding:9px 22px;border:none;background:var(--accent-gradient,linear-gradient(135deg,#38bdf8,#818cf8));color:#fff;border-radius:10px;font-family:inherit;font-size:14px;font-weight:600;cursor:pointer">' + _esc(t('dl')) + '</button>'
                + '</div>';
            overlay.appendChild(box);

            function close(answer) {
                try { document.body.removeChild(overlay); } catch(_){}
                document.removeEventListener('keydown', onKey, true);
                emitTele(answer ? 'consent_accept' : 'consent_decline', { slug: slug, key: key, sizeMB: sizeMB });
                resolve(answer);
            }
            function onKey(e){
                if (e.key === 'Escape') { e.preventDefault(); close(false); }
                else if (e.key === 'Enter') { e.preventDefault(); close(true); }
            }
            box.querySelector('.ham-ok').addEventListener('click', function(){ close(true); });
            box.querySelector('.ham-cancel').addEventListener('click', function(){ close(false); });
            overlay.addEventListener('click', function(e){ if (e.target === overlay) close(false); });
            document.addEventListener('keydown', onKey, true);

            document.body.appendChild(overlay);
            // Defer focus so screen readers announce dialog before button focus.
            setTimeout(function(){ try { box.querySelector('.ham-ok').focus(); } catch(_){} }, 50);
        });
    }

    function _esc(s) {
        return String(s||'').replace(/[&<>"']/g, function(c){
            return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c];
        });
    }

    /**
     * Prepare a heavy asset for use. Returns {urls, fromCache} when ready,
     * rejects if device unsupported / user declined / network failed.
     *
     * @param {string} key — unique asset key, e.g. 'dfn3', 'demucs-htdemucs', 'whisper-tiny'
     * @param {object} opts {
     *   urls:           Object<filename, primaryUrl>     // required
     *   urlsFallback:   Object<filename, fallbackUrl>    // optional
     *   sizeMB:         number — total combined          // required
     *   slug:           string — owning tool slug        // required
     *   modelLabel:     string — display name in modal   // optional, defaults to key
     *   modelDesc:      string — short description       // optional
     *   minDeviceMemMB: number — gate                    // optional, derived from sizeMB
     *   blockediOSSafari: boolean — gate                 // optional
     *   requiresAudioWorklet: boolean — gate             // optional
     *   requiresWebGPU: boolean — gate                   // optional
     *   skipPrompt:     boolean — bypass consent (for re-prepare after user already consented earlier)
     * }
     */
    async function prepare(key, opts) {
        opts = opts || {};
        if (typeof key !== 'string' || !key) throw new Error('asset key required');
        if (!opts.urls || typeof opts.urls !== 'object') throw new Error('opts.urls required');
        if (typeof opts.sizeMB !== 'number' || opts.sizeMB <= 0) throw new Error('opts.sizeMB required');
        if (typeof opts.slug !== 'string' || !opts.slug) throw new Error('opts.slug required');

        // Already prepared this session → fast-path.
        if (_loaded[key]) return _loaded[key];

        // Device gate.
        if (!window.__deviceCap || !window.__deviceCap.canRunHeavyML(opts.sizeMB, {
            requiresAudioWorklet: opts.requiresAudioWorklet,
            blockediOSSafari: opts.blockediOSSafari,
            requiresWebGPU: opts.requiresWebGPU,
            requiresSAB: opts.requiresSAB
        })) {
            emitTele('device_gate_blocked', { slug: opts.slug, key: key, sizeMB: opts.sizeMB });
            var err = new Error('Device cannot run this model');
            err.code = 'DEVICE_UNSUPPORTED';
            throw err;
        }

        // Consent gate (prompt only if size warrants it AND not previously consented).
        var needPrompt = !opts.skipPrompt
            && opts.sizeMB >= SIZE_PROMPT_THRESHOLD_MB
            && !_hasConsent(key);
        if (needPrompt) {
            // Re-prompt suppressor: if user declined this session, don't immediately
            // re-prompt on next call. Caller can clear via resetConsent(key).
            if (_declinedThisSession[key]) {
                var declinedErr = new Error('User declined consent (this session)');
                declinedErr.code = 'CONSENT_DECLINED';
                throw declinedErr;
            }
            var ok = await _showConsent({
                slug: opts.slug,
                key: key,
                sizeMB: opts.sizeMB,
                modelLabel: opts.modelLabel,
                modelDesc: opts.modelDesc
            });
            if (!ok) {
                _declinedThisSession[key] = true;
                var declineErr = new Error('User declined consent');
                declineErr.code = 'CONSENT_DECLINED';
                throw declineErr;
            }
            _setConsent(key, true);
        }

        // Pre-flight HEAD on each primary URL → record availability + size.
        // We don't actually download here — that's the worker's job. We just
        // verify reachability. If primary fails, swap to fallback URL.
        var t0 = performance.now();
        var resolvedUrls = {};
        var usedFallback = false;
        var keys = Object.keys(opts.urls);

        for (var i = 0; i < keys.length; i++) {
            var fname = keys[i];
            var primary = opts.urls[fname];
            var fallback = opts.urlsFallback && opts.urlsFallback[fname];
            try {
                await _probeUrl(primary, opts.signal);
                resolvedUrls[fname] = primary;
            } catch (e) {
                if (!fallback) throw _wrapErr('PRIMARY_UNREACHABLE', primary, e);
                try {
                    await _probeUrl(fallback, opts.signal);
                    resolvedUrls[fname] = fallback;
                    usedFallback = true;
                } catch (e2) {
                    throw _wrapErr('BOTH_UNREACHABLE', primary + ' / ' + fallback, e2);
                }
            }
        }

        var result = {
            urls: resolvedUrls,
            fromCache: false, // unknown — SW handles caching, we don't introspect
            usedFallback: usedFallback,
            ms: Math.round(performance.now() - t0)
        };
        _loaded[key] = result;
        emitTele('prepare_ok', {
            slug: opts.slug, key: key, sizeMB: opts.sizeMB,
            ms: result.ms, usedFallback: usedFallback ? 1 : 0
        });
        return result;
    }

    /**
     * HEAD request to verify URL reachability without downloading bytes.
     * Times out after 8 s — we'd rather try the fallback URL than wait.
     *
     * Some CORS configurations refuse HEAD but allow GET (or vice versa).
     * If HEAD fails with a CORS-shaped error, fall back to a GET range
     * request for the first 1 byte — also cheap, more compatible.
     */
    function _probeUrl(url, abortSignal) {
        return new Promise(function(resolve, reject) {
            var controller = new AbortController();
            if (abortSignal) {
                if (abortSignal.aborted) { reject(_abortErr()); return; }
                abortSignal.addEventListener('abort', function(){ controller.abort(); });
            }
            var timeoutId = setTimeout(function(){ controller.abort(); }, 8000);

            fetch(url, { method: 'HEAD', signal: controller.signal, mode: 'cors' })
                .then(function(r){
                    clearTimeout(timeoutId);
                    if (r.ok) resolve();
                    else reject(new Error('HEAD ' + r.status));
                })
                .catch(function(headErr){
                    // Some CDNs (HF on certain LFS paths) reject HEAD; try ranged GET.
                    var rangeController = new AbortController();
                    if (abortSignal) abortSignal.addEventListener('abort', function(){ rangeController.abort(); });
                    var rangeTimeout = setTimeout(function(){ rangeController.abort(); }, 8000);
                    fetch(url, { method: 'GET', headers: { 'Range': 'bytes=0-0' }, signal: rangeController.signal, mode: 'cors' })
                        .then(function(r){
                            clearTimeout(rangeTimeout);
                            if (r.ok || r.status === 206) resolve();
                            else reject(new Error('Range GET ' + r.status + ' (HEAD: ' + (headErr.message||'') + ')'));
                        })
                        .catch(function(rangeErr){
                            clearTimeout(rangeTimeout);
                            reject(rangeErr);
                        });
                });
        });
    }

    function _wrapErr(code, url, cause) {
        var e = new Error(code + ' for ' + (url||'') + ': ' + ((cause && cause.message) || cause));
        e.code = code;
        e.cause = cause;
        return e;
    }
    function _abortErr() { var e = new Error('Aborted'); e.code = 'ABORTED'; return e; }

    /**
     * Status query — useful for UI to show "Model already on your device" badge.
     */
    function getStatus(key, opts) {
        opts = opts || {};
        if (_loaded[key]) return STATUS.LOADED;
        if (_declinedThisSession[key]) return STATUS.DECLINED;
        if (_hasConsent(key)) return STATUS.CONSENTED;
        // Device gate check requires sizeMB — caller must pass via opts to get this answer.
        if (typeof opts.sizeMB === 'number' && window.__deviceCap) {
            if (!window.__deviceCap.canRunHeavyML(opts.sizeMB, opts)) return STATUS.UNAVAILABLE;
        }
        return STATUS.AVAILABLE;
    }

    function resetConsent(key) {
        _setConsent(key, false);
        delete _declinedThisSession[key];
        delete _loaded[key];
    }

    window.__heavyAsset = Object.freeze({
        prepare: prepare,
        getStatus: getStatus,
        resetConsent: resetConsent,
        STATUS: STATUS,
        SIZE_PROMPT_THRESHOLD_MB: SIZE_PROMPT_THRESHOLD_MB
    });
})();
